/**
 * Token persistence, isolated behind one module.
 *
 * Everything reads and writes tokens through here, so switching to
 * httpOnly cookies later touches this file only.
 */

const ACCESS_KEY = 'vms.access_token';
const REFRESH_KEY = 'vms.refresh_token';
// When the server says the session ends: idle (moves forward on each refresh)
// and absolute (fixed at sign-in). Stored with the tokens so every tab sees
// the deadline the last refresh - from any tab - set.
const IDLE_KEY = 'vms.session_expires_at';
const ABSOLUTE_KEY = 'vms.session_absolute_expires_at';
const GUEST_KEY = 'vms.guest_token';

function read(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    // Private browsing or disabled storage — degrade to in-memory only.
    return null;
  }
}

function write(key, value) {
  try {
    if (value === null || value === undefined) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

export const tokenStore = {
  getAccessToken: () => read(ACCESS_KEY),
  getRefreshToken: () => read(REFRESH_KEY),

  setTokens({
    access_token: accessToken,
    refresh_token: refreshToken,
    session_expires_at: idleAt,
    session_absolute_expires_at: absoluteAt,
  }) {
    if (accessToken) write(ACCESS_KEY, accessToken);
    if (refreshToken) write(REFRESH_KEY, refreshToken);
    if (idleAt) write(IDLE_KEY, idleAt);
    if (absoluteAt) write(ABSOLUTE_KEY, absoluteAt);
  },

  /** Epoch milliseconds, or null when the server has not said (older sessions). */
  getSessionDeadlines() {
    const parse = (value) => (value ? Date.parse(value) || null : null);
    return { idleAt: parse(read(IDLE_KEY)), absoluteAt: parse(read(ABSOLUTE_KEY)) };
  },

  clear() {
    write(ACCESS_KEY, null);
    write(REFRESH_KEY, null);
    write(IDLE_KEY, null);
    write(ABSOLUTE_KEY, null);
  },

  isAuthenticated: () => Boolean(read(ACCESS_KEY)),

  /**
   * Anonymous shoppers get a stable token so their server-side cart survives a
   * refresh, and can be merged into their account if they later sign up.
   */
  getOrCreateGuestToken() {
    let token = read(GUEST_KEY);
    if (!token) {
      token = crypto.randomUUID();
      write(GUEST_KEY, token);
    }
    return token;
  },

  getGuestToken: () => read(GUEST_KEY),
  clearGuestToken: () => write(GUEST_KEY, null),
};
